import * as t from 'io-ts';
import _, {mapValues, omit} from 'lodash';

import {makeApiType} from './apiTypes';

/*
 * Runtime type.
 */

export const RBadgeCounters = t.type({
  updatedAt: t.union([t.Integer, t.undefined]),
  values: t.dictionary(
    t.string,
    t.union([t.undefined, t.intersection([t.type({value: t.Integer}), t.partial({uid: t.string})])]),
  ),
});

export const RBoldCounters = t.type({
  updatedAt: t.union([t.Integer, t.undefined]),
  values: t.dictionary(
    t.string,
    t.union([t.undefined, t.intersection([t.type({value: t.boolean}), t.partial({uid: t.string})])]),
  ),
});

const baseCounters = {
  badge: RBadgeCounters,
  badgeUnread: RBadgeCounters,
  bold: RBoldCounters,
};

export const RCounters = t.readonly(
  t.type({
    notifications: t.Integer,
    ...baseCounters,
    delegations: t.dictionary(t.string, RBadgeCounters),
  }),
  'counters',
);

const RUserCounters = t.readonly(
  t.type({
    ...baseCounters,
  }),
);

export const RBackendCountersCheck = t.readonly(
  t.type({
    backendCounter: t.union([t.Int, t.undefined]),
    backendGround: t.union([t.Int, t.undefined]),
    counterType: t.union([t.literal('open'), t.literal('unread'), t.literal('unassigned'), t.undefined]),
    counterView: t.union([t.string, t.undefined]),
    counterIds: t.union([t.undefined, t.record(t.string, t.Int)]),
    counterUpdateUid: t.union([t.string, t.undefined]),
    skipReason: t.union([t.string, t.undefined]),
    timeElapsedSinceUidUpdateMs: t.union([t.number, t.null, t.undefined]),
    timeElapsedSinceLatestConversationUpdateMs: t.union([t.number, t.null, t.undefined]),
    context: t.union([t.string, t.undefined]),
    countersPreferences: t.type({
      individual: t.union([t.literal('open'), t.literal('unread')]),
      team: t.union([t.literal('open'), t.literal('unassigned')]),
    }),
  }),
);

/*
 * Type.
 */

export interface BadgeCountersModel extends t.TypeOf<typeof RBadgeCounters> {}
export interface BoldCountersModel extends t.TypeOf<typeof RBoldCounters> {}
export interface CountersModel extends t.TypeOf<typeof RCounters> {}
export interface UserCountersModel extends t.TypeOf<typeof RUserCounters> {}
export interface BackendCountersCheckModel extends t.TypeOf<typeof RBackendCountersCheck> {}

/*
 * IO methods.
 */

function mapBackendCounters(counters: any) {
  if (!counters) {
    return {};
  }

  return mapValues(counters, (value) => ({
    value,
    uid: undefined,
  }));
}

export function parseCountersFromBoot(src: any, delegations: any): CountersModel {
  let openValues;
  let unreadValues;
  let unseenValues;

  if (src.counters.open_v2 && Object.keys(src.counters.open_v2).length > 0) {
    openValues = src.counters.open_v2;
  } else {
    openValues = mapBackendCounters(src.counters.open);
  }

  if (src.counters.unread_v2 && Object.keys(src.counters.unread_v2).length > 0) {
    unreadValues = src.counters.unread_v2;
  } else {
    unreadValues = mapBackendCounters(src.counters.unread);
  }

  if (src.counters.unseen_v2 && Object.keys(src.counters.unseen_v2).length > 0) {
    unseenValues = src.counters.unseen_v2;
  } else {
    unseenValues = mapBackendCounters(src.counters.unseen);
  }

  return {
    notifications: src.num_unseen_notifications || 0,
    badge: {
      updatedAt: src.ts,
      values: openValues,
    },
    badgeUnread: {
      updatedAt: src.ts,
      values: unreadValues,
    },
    bold: {
      updatedAt: src.ts,
      values: unseenValues,
    },
    delegations: _(delegations)
      .keyBy((d) => d.id)
      .mapValues((d) => {
        let values;
        if (d.counters_v2 && Object.keys(d.counters_v2).length > 0) {
          values = d.counters_v2;
        } else {
          values = mapBackendCounters(d.counters);
        }

        return {
          updatedAt: src.ts,
          values,
        };
      })
      .value(),
  };
}

function parseCounters(src: any) {
  let counterValues;
  const payloadVersion = src.version;
  if (!payloadVersion || payloadVersion === 1) {
    counterValues = mapBackendCounters(omit(src, ['sent_at', 'delegation_id', '_push_meta']));
  } else if (src.version === 2) {
    counterValues = src.counters;
  } else {
    counterValues = {};
  }

  return {
    updatedAt: src.sent_at || undefined,
    values: counterValues,
  };
}

/*
 * API type.
 */

export const ApiBadgeCounters = makeApiType('badgeCounters', RBadgeCounters, parseCounters);
export const ApiBoldCounters = makeApiType('boldCounters', RBoldCounters, parseCounters);
